import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert, Mail, LogOut, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Phase 8: full-screen suspended-account view. Rendered by the App-level
 * gate whenever the authed user is banned — no navbar, no routes, no app
 * features. The backend blocks everything except /auth/me and the appeal
 * endpoints, so this page is presentation on top of real enforcement.
 */
export default function Suspended() {
  const { logout } = useAuth();
  const [appeal, setAppeal] = useState(undefined); // undefined = loading
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.myAppeal().then((d) => setAppeal(d.appeal)).catch(() => setAppeal(null));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (text.trim().length < 10) return toast.error("Tell us a little more — at least 10 characters");
    setSending(true);
    try {
      await api.submitAppeal(text.trim());
      setText("");
      toast.success("Appeal sent — our team will review it");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your appeal — try again");
      if (e?.response?.status === 409) load();
    } finally {
      setSending(false);
    }
  };

  const pending = appeal?.status === "pending";
  const rejected = appeal?.status === "rejected";

  return (
    <div data-testid="suspended-page" className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <Seo title="Account suspended" />
      <div className="max-w-lg w-full bg-white border border-neutral-200 rounded-2xl p-8 sm:p-10">
        <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <h1 className="font-display text-3xl text-forest mt-5 tracking-tight">Account suspended</h1>
        <p className="text-neutral-600 text-sm mt-3 leading-relaxed">
          Your CivicHearth account has been suspended and access to the platform is paused.
          If you think this is a mistake, you can appeal below — a member of our team will review it.
        </p>
        <a
          data-testid="suspended-contact"
          href="mailto:hello@civichearth.com"
          className="inline-flex items-center gap-2 text-sm text-forest font-medium mt-4 hover:underline"
        >
          <Mail className="w-4 h-4" /> hello@civichearth.com
        </a>

        <div className="mt-7 border-t border-neutral-100 pt-6">
          {appeal === undefined ? null : pending ? (
            <div data-testid="appeal-pending" className="flex items-start gap-3 bg-forest/5 border border-forest/15 rounded-xl p-4">
              <Clock className="w-5 h-5 text-forest shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-forest">Appeal received</div>
                <p className="text-xs text-neutral-600 mt-1">
                  Our team is reviewing it — you'll be notified of the outcome. Only one appeal can be open at a time.
                </p>
              </div>
            </div>
          ) : (
            <>
              {rejected && (
                <div data-testid="appeal-rejected" className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
                  <div className="text-sm font-medium text-red-700">Your previous appeal was declined</div>
                  {appeal.reason && (
                    <p data-testid="appeal-rejected-reason" className="text-xs text-red-600 mt-1">
                      Reason: {appeal.reason}
                    </p>
                  )}
                </div>
              )}
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">
                {rejected ? "Submit a new appeal" : "Appeal this decision"}
              </label>
              <Textarea
                data-testid="appeal-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Tell us why you believe your account should be restored…"
                className="mt-2"
              />
              <Button
                data-testid="appeal-submit"
                onClick={submit}
                disabled={sending}
                className="mt-3 rounded-full bg-forest hover:bg-forest-hover"
              >
                {sending ? "Sending…" : "Send appeal"}
              </Button>
            </>
          )}
        </div>

        <button
          data-testid="suspended-logout"
          onClick={logout}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-forest"
        >
          <LogOut className="w-3.5 h-3.5" /> Log out
        </button>
      </div>
    </div>
  );
}
